import type { Locale } from "../i18n/locale";
import type { LineItem } from "./types";

/** Approximate on-demand Linux us-east-1 $/hour (indicative). */
export const EC2_SKUS = [
  {
    id: "t3.micro",
    hourlyUsd: 0.0104,
    labelUk: "t3.micro (2 vCPU burstable)",
    labelEn: "t3.micro (2 vCPU burstable)",
  },
  { id: "t3.small", hourlyUsd: 0.0208, labelUk: "t3.small", labelEn: "t3.small" },
  { id: "t3.medium", hourlyUsd: 0.0416, labelUk: "t3.medium", labelEn: "t3.medium" },
  {
    id: "c7g.large",
    hourlyUsd: 0.0723,
    labelUk: "c7g.large (Graviton, compute)",
    labelEn: "c7g.large (Graviton, compute)",
  },
  {
    id: "m7g.xlarge",
    hourlyUsd: 0.1638,
    labelUk: "m7g.xlarge (Graviton, general)",
    labelEn: "m7g.xlarge (Graviton, general)",
  },
] as const;

export type Ec2SkuId = (typeof EC2_SKUS)[number]["id"];

export const RDS_SKUS = [
  {
    id: "db.t3.micro",
    hourlyUsd: 0.017,
    labelUk: "db.t3.micro",
    labelEn: "db.t3.micro",
  },
  {
    id: "db.t3.small",
    hourlyUsd: 0.034,
    labelUk: "db.t3.small",
    labelEn: "db.t3.small",
  },
  {
    id: "db.t3.medium",
    hourlyUsd: 0.068,
    labelUk: "db.t3.medium",
    labelEn: "db.t3.medium",
  },
  {
    id: "db.m7g.large",
    hourlyUsd: 0.152,
    labelUk: "db.m7g.large (Graviton)",
    labelEn: "db.m7g.large (Graviton)",
  },
] as const;

export type RdsSkuId = (typeof RDS_SKUS)[number]["id"];

const HOURS_PER_MONTH = 730;
/** gp3-ish storage $/GB-mo (us-east-1 ballpark). */
const RDS_STORAGE_USD_PER_GB = 0.115;

export type AddonInfraConfig = {
  ec2Enabled: boolean;
  ec2Sku: Ec2SkuId;
  ec2Count: number;
  rdsEnabled: boolean;
  rdsSku: RdsSkuId;
  rdsStorageGb: number;
};

export const DEFAULT_ADDON_INFRA: AddonInfraConfig = {
  ec2Enabled: false,
  ec2Sku: "t3.medium",
  ec2Count: 1,
  rdsEnabled: false,
  rdsSku: "db.t3.small",
  rdsStorageGb: 20,
};

const EC2_IDS = new Set<string>(EC2_SKUS.map((s) => s.id));
const RDS_IDS = new Set<string>(RDS_SKUS.map((s) => s.id));

export function normalizeAddonInfra(x: unknown): AddonInfraConfig {
  if (!x || typeof x !== "object") return { ...DEFAULT_ADDON_INFRA };
  const o = x as Record<string, unknown>;
  const ec2Sku =
    typeof o.ec2Sku === "string" && EC2_IDS.has(o.ec2Sku)
      ? (o.ec2Sku as Ec2SkuId)
      : DEFAULT_ADDON_INFRA.ec2Sku;
  const rdsSku =
    typeof o.rdsSku === "string" && RDS_IDS.has(o.rdsSku)
      ? (o.rdsSku as RdsSkuId)
      : DEFAULT_ADDON_INFRA.rdsSku;
  const ec2Count = Math.min(48, Math.max(1, Math.round(Number(o.ec2Count) || 1)));
  const rdsStorageGb = Math.min(
    2000,
    Math.max(20, Math.round(Number(o.rdsStorageGb) || 20)),
  );
  return {
    ec2Enabled: Boolean(o.ec2Enabled),
    ec2Sku,
    ec2Count,
    rdsEnabled: Boolean(o.rdsEnabled),
    rdsSku,
    rdsStorageGb,
  };
}

function findEc2(id: Ec2SkuId) {
  return EC2_SKUS.find((s) => s.id === id) ?? EC2_SKUS[2];
}

function findRds(id: RdsSkuId) {
  return RDS_SKUS.find((s) => s.id === id) ?? RDS_SKUS[1];
}

/**
 * Monthly USD before regional factor (priced like us-east-1 on-demand).
 */
export function addonInfraLineItems(
  config: AddonInfraConfig,
  locale: Locale,
): { items: LineItem[]; assumptions: string[] } {
  const items: LineItem[] = [];
  const assumptions: string[] = [];

  if (config.ec2Enabled) {
    const sku = findEc2(config.ec2Sku);
    const monthly = config.ec2Count * sku.hourlyUsd * HOURS_PER_MONTH;
    const label =
      locale === "uk" ? `Amazon EC2 (${sku.labelUk})` : `Amazon EC2 (${sku.labelEn})`;
    const detail =
      locale === "uk"
        ? `${config.ec2Count} × on-demand, ~${HOURS_PER_MONTH} год/міс, Linux; орієнтир us-east-1`
        : `${config.ec2Count} × on-demand, ~${HOURS_PER_MONTH} h/mo, Linux; us-east-1 reference`;
    items.push({ id: "ec2", label, monthlyUsd: monthly, detail });
    assumptions.push(
      locale === "uk"
        ? `Додаток EC2: ${config.ec2Count} × ${sku.id}, без EBS/ELB/NAT у рядку (додай окремо за потреби).`
        : `EC2 add-on: ${config.ec2Count} × ${sku.id}; EBS/ELB/NAT not itemized.`,
    );
  }

  if (config.rdsEnabled) {
    const sku = findRds(config.rdsSku);
    const instMonthly = sku.hourlyUsd * HOURS_PER_MONTH;
    const storageMonthly = config.rdsStorageGb * RDS_STORAGE_USD_PER_GB;
    const monthly = instMonthly + storageMonthly;
    const label =
      locale === "uk" ? `Amazon RDS (${sku.labelUk})` : `Amazon RDS (${sku.labelEn})`;
    const detail =
      locale === "uk"
        ? `Клас ${sku.id}, ~${HOURS_PER_MONTH} год/міс + ~${config.rdsStorageGb} GB gp3; single-AZ орієнтир`
        : `Class ${sku.id}, ~${HOURS_PER_MONTH} h/mo + ~${config.rdsStorageGb} GB gp3; single-AZ reference`;
    items.push({ id: "rds", label, monthlyUsd: monthly, detail });
    assumptions.push(
      locale === "uk"
        ? `Додаток RDS: ${sku.id}, ${config.rdsStorageGb} GB; без Multi-AZ read replicas у формулі.`
        : `RDS add-on: ${sku.id}, ${config.rdsStorageGb} GB; Multi-AZ/replicas not itemized.`,
    );
  }

  return { items, assumptions };
}
